import { InvalidBannerError } from '../errors/InvalidBannerError';

/**
 * The homepage banner is a singleton site setting (one image, on/off), not
 * a collection of banners — so unlike every other content feature in this
 * app there is exactly one row, always addressed by the same fixed id.
 * `getForAdmin()`/`save()` on the repository always operate on this id.
 */
export const BANNER_ID = 'homepage-banner';

export interface BannerInput {
  id: string;
  imageUrl: string | null;
  altText: string;
  isActive: boolean;
  updatedAt: string;
}

/**
 * Real business rule: a banner cannot be turned on without an image — an
 * "active" banner with no image would render as a broken/empty band on the
 * homepage. Turning it off (isActive: false) is always valid regardless of
 * whether an image is set, so an admin can prepare and immediately hide a
 * banner, or disable one without losing the configured image URL.
 */
export class Banner {
  private constructor(public readonly props: BannerInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: BannerInput): Banner {
    const altText = input.altText.trim();
    const imageUrl = input.imageUrl?.trim() || null;

    if (input.isActive && !imageUrl) {
      throw new InvalidBannerError('an active banner must have an image URL.');
    }
    if (input.isActive && altText.length === 0) {
      throw new InvalidBannerError('an active banner must have alt text for accessibility.');
    }

    return new Banner({ ...input, id: BANNER_ID, imageUrl, altText });
  }
}
